<template>
  <div class="main-images-entidad">
    <UCPreload
      :show="isLoadingData"
      :number="3"
    />

    <template v-if="!isLoadingData">
      <div
        v-if="attachments.length === 0"
        class="content-no-data"
      >
        {{ $t('entityAttachmentsData.noFiles') }}
      </div>

      <template v-else>
        <div
          v-for="(group, i) in groupedAttachments"
          :key="i"
          class="content-attachments"
        >
          <div
            v-if="group.data.length > 0"
            class="content-attachments-span"
          >
            <span> {{ group.label }}</span>
          </div>

          <div
            class="content-attachments-media"
            :class="{
              noMobile: !isMobile,
              mobile: isMobile,
            }"
          >
            <div
              v-for="(attachment, j) in group.data"
              :key="attachment.Id"
              class="content-attachments-media__datos"
            >
              <img
                class="content-attachments-media__datos--show"
                :src="getAttachmentPreview(attachment)"
                loading="lazy"
                width="190"
                height="190"
                @click="openAttachment(attachment, openPdfCallback)"
              >
              <span class="content-attachments-media__span margin-left-half">
                {{ getAttachmentDescription(attachment, j + 1) }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup>
// @ts-check
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useEntityAttachmentsData } from '@/composables/useEntityAttachmentsData';
import { useI18n } from 'vue-i18n';

import UCPreload from '@/components/common/UCPreload.vue';

/** @import { Imagen } from '@erpagroapp/types' */

/**
 * @typedef {object} AttachmentsData
 * @property {string} label - The label.
 * @property {Imagen[]} data - The attachments.
 */

/**
 * @typedef {object} Attachments
 * @property {AttachmentsData} images - The images.
 * @property {AttachmentsData} archives - The archives.
 */

const props = defineProps({
  entityId: {
    type: Number,
    default: null,
  },
  entityType: {
    type: String,
    default: 'PedidoVenta',
  },
  isActive: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'loadingError',
  'openPdf',
]);

const { t } = useI18n();

const {
  attachments,
  isLoadingData,
  loadAttachmentsData,
  openAttachment,
  getAttachmentPreview,
  getAttachmentDescription,
  isAttachmentPDF,
} = useEntityAttachmentsData();

const isMobile = ref(false);

const groupedAttachments = computed(() => {
  const images = [];
  const archives = [];

  for (const item of attachments.value) {
    if (isAttachmentPDF(item)) {
      archives.push(item);
    } else {
      images.push(item);
    }
  }

  /** @type {Attachments} */
  const grouped = {
    images: {
      label: t('common.images'),
      data: images,
    },
    archives: {
      label: 'PDF',
      data: archives,
    },
  };

  return grouped;
});

async function loadAndGroupData() {
  try {
    const successful = await loadAttachmentsData(props.entityId, props.entityType);

    if (successful) {
      isMobile.value = window.innerWidth < 768;
    }
  } catch {
    emit('loadingError');
  }
}

function openPdfCallback() {
  emit('openPdf');
}

watch(() => props.entityId, (newVal) => {
  if (!props.isActive) {
    return;
  }

  if (newVal != null) {
    loadAndGroupData();
  }
});

onMounted(() => {
  if (typeof props.entityId === 'number' && props.isActive) {
    loadAndGroupData();
  }
});
</script>

<style scoped lang="scss">
.main-images-entidad {
  .content-attachments {
    .content-attachments-span {
      padding-left: 18px;
      padding-top: 10px;
      padding-bottom: 10px;
      background-color: var(--his-steel-blue);
      color: var(--his-whispering-lilac);
      font-size: 16px;
      text-transform: uppercase;
    }

    .content-attachments-media {
      &__span {
        font-size: 15px;
        color: var(--his-steel-blue);
      }

      &__datos {
        display: flex;
        flex-direction: column;
        padding-bottom: 5px;

        &--show {
          margin: 5px;
          object-fit: cover;
        }
      }
    }
  }

  .content-no-data {
    padding-top: 10px;
    padding-left: 20px;
  }
}

.content-attachments-media.mobile {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1/5fr;
  grid-column-gap: 5px;
}

.content-attachments-media.noMobile {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1/5fr;
  grid-column-gap: 5px;
}
</style>
